// src/data/gcDesk.js
//
// THE ORDERING AND AGGREGATION BEHIND THE GENERAL CHAIRPERSON'S DESK (weekend plan, batch 3).
// Pure functions - no React, no Firestore - so the logic that decides what a General Chairperson is
// shown, and in what order, is executed by scripts\test-gc-desk.mjs rather than eyeballed in a
// browser. src\pages\GCDesk.js renders what this returns and adds nothing to it. Same split as
// src\data\deskQueue.js, deliberately, so the two desks cannot drift apart in method.
//
// THE FOUR THINGS ONLY A GENERAL CHAIRPERSON DOES, which is what this module computes:
//   1. the unrouted queue, WITH the reassign control
//   2. unheld seats AND how much work is pointed at them
//   3. the committee readiness surface (batch 1 - the page embeds it, this module does not)
//   4. the arbitration docket and its clock
//
// THE RULE THAT GOVERNS EVERY FUNCTION HERE, inherited from deskQueue.js (PD-4): an unknown is
// never rendered as a zero and never as a safe value. A seat whose holder is not of record is NOT
// reported as vacant. A docket row that is not watched is NOT reported as clear. Absent is printed
// as absent.

import { claimFiledAtSeconds } from './claimPayload';
import { LC_ROSTER } from './lcRoster';
import { ARBITRATION_DOCKET } from './arbitrationDocket';
import { daysUntilISO, daysSince, isUnrouted } from './deskQueue';

export { daysUntilISO, daysSince, isUnrouted };

// A docket row inside this many days of its submission date is the top of the page. Same shape and
// the same status as WINDOW_URGENT_DAYS in deskQueue.js: TWENTY-ONE DAYS IS THE ASSISTANT'S CHOICE
// AND NOT BOB'S RULING. It matches the horizon checkArbitrationDocket() already reports on ("none
// due within 21 days"), so the page and the verifier speak about the same window rather than two.
// One constant; overturning it is a one-line edit.
export const DOCKET_SOON_DAYS = 21;

// THE THREE SEAT STATES, and keeping them three is the whole point of this module.
//
//   HELD           a name is on the roster for this seat.
//   VACANT         nobody sits in it. No account carries the scopeKey, so no human sees a claim
//                  routed here. Under ruling 60 the work and the authority vest in the GC.
//   NOT_OF_RECORD  the platform does not record WHO holds it (the ruling 54 placeholder). Routing
//                  depends on an account carrying the scopeKey, not on the roster printing a name.
//
// Folding NOT_OF_RECORD into VACANT would flag 10 of the 11 GO-342 seats and make the signal
// useless on the one seat that is genuinely empty. Folding it into HELD would assert a holder the
// platform does not have. It is its own value because it is its own fact.
export function seatState(rosterRow) {
  const label = String((rosterRow && rosterRow.label) || '');
  if (!rosterRow) return 'UNKNOWN';
  if (/\(VACANT\b/.test(label)) return 'VACANT';
  if (/\(NOT OF RECORD\b/i.test(label)) return 'NOT_OF_RECORD';
  return 'HELD';
}

// The reassign picklist for one unrouted claim, narrowed exactly as lcRoster.js says it should be:
// craft + railroadKey. A claim missing either field gets the WHOLE roster rather than an empty
// list - an empty picker on a claim that needs routing is a dead end, and the officer choosing from
// eleven rows is not harmed. Never returns a seat that is not on the roster.
export function reassignCandidates(claim, roster) {
  const list = Array.isArray(roster) ? roster : LC_ROSTER;
  const craft = String((claim && claim.craft) || '').trim();
  const railroad = String((claim && claim.railroad) || '').trim();
  if (!craft && !railroad) return list.slice();
  const narrowed = list.filter(lc =>
    (!craft || String(lc.craft || '') === craft) &&
    (!railroad || String(lc.railroadKey || '') === railroad));
  // A narrowing that eliminates everything has told the officer nothing useful. Fall back to the
  // full roster and let the page say the narrowing found no match, rather than showing an empty
  // control that looks like "no chairperson exists".
  return narrowed.length ? narrowed : list.slice();
}

// THE FIELDS A REASSIGNMENT WRITES, as a pure function, so the payload is under test rather than
// assembled inline in a click handler.
//
// THIS IS THE PART THAT CAN BREAK SOMETHING ELSE, and it is why it lives here.
//
//   scopeKey        the jurisdiction. The deployed rules permit a General Chairperson to change it
//                   within his own committee (firestore.rules, gcEdit) - and permit NOBODY else to.
//                   Ruling 61.10 is not contradicted: scopeKey is jurisdiction, and re-jurisdicting
//                   a claim that resolved to NO seat is the one legitimate use of that.
//   routingStatus   'unrouted' -> 'routed'. Left stale, every query and count that keys on it - the
//                   LC desk, this page, Claims - keeps reporting a routed claim as unrouted.
//   localCommittee  A PROJECTION OF scopeKey (routing.js) AND A VERIFIER CHECK. CLM-2 asserts that
//                   every claim's localCommittee agrees with its scopeKey projection. Writing the
//                   new scopeKey without recomputing this would leave the two disagreeing and turn
//                   CLM-2 red on the very next ycp.cmd - a silent data defect caught only because
//                   the check happens to exist. Recomputed here from the key itself, with
//                   routing.js's own regex rather than a second convention (SS XI).
//
//   committee       DELIBERATELY ABSENT. gcEdit() requires it unchanged, and a GC moving a claim
//                   between general committees is not a routing fix - it is a different act with
//                   different authority behind it.
//   createdBy /
//   createdAt       DELIBERATELY ABSENT. coreIntact() requires both unchanged.
//
// Returns null - never a partial patch - when the target is not a well-formed Local Chairperson
// scope key. A patch that half-applies is worse than one that does not apply at all.
export function reassignPatch(scopeKey) {
  const sk = String(scopeKey || '').trim();
  const m = /^LC(\d+)-(\d{3})[A-Z]?$/.exec(sk);
  if (!m) return null;
  return { scopeKey: sk, routingStatus: 'routed', localCommittee: m[1] };
}

// How much work is pointed at each seat in the committee, and what state that seat is in.
// EVERY ROSTER SEAT IS RETURNED, including seats with no claims, because "this vacant seat has
// nothing pointed at it" and "this vacant seat has four claims" are different problems and a
// zero-claim seat that is empty still needs filling.
export function seatLoad(claims, roster, opts) {
  const o = opts || {};
  const list = Array.isArray(roster) ? roster : LC_ROSTER;
  const nowMs = typeof o.nowMs === 'number' ? o.nowMs : Date.now();
  const cl = Array.isArray(claims) ? claims : [];

  const byKey = new Map();
  list.forEach(lc => byKey.set(lc.scopeKey, {
    scopeKey: lc.scopeKey,
    label: lc.label,
    craft: lc.craft,
    railroadKey: lc.railroadKey,
    state: seatState(lc),
    claims: [],
    oldestAgeDays: null,
    soonestWindowDays: null,
  }));

  cl.forEach(c => {
    const sk = String((c && c.scopeKey) || '');
    const seat = byKey.get(sk);
    if (!seat) return;                       // unrouted, or a key with no roster row
    const left = daysUntilISO(c && c.expiresAt, nowMs);
    const age = daysSince(claimFiledAtSeconds(c), nowMs);
    seat.claims.push({ claim: c, left, age });
    if (age !== null && (seat.oldestAgeDays === null || age > seat.oldestAgeDays)) seat.oldestAgeDays = age;
    if (left !== null && (seat.soonestWindowDays === null || left < seat.soonestWindowDays)) seat.soonestWindowDays = left;
  });

  const seats = Array.from(byKey.values());
  // A seat nobody holds, carrying work, sorted by how much - that is the queue the GC works.
  // Vacant first, then not-of-record, then held; within a state, most claims first.
  const rank = { VACANT: 0, NOT_OF_RECORD: 1, HELD: 2, UNKNOWN: 3 };
  seats.sort((a, b) => (rank[a.state] - rank[b.state]) || (b.claims.length - a.claims.length) ||
                       a.scopeKey.localeCompare(b.scopeKey));

  // A scopeKey carried by a claim that matches NO roster row at all. Not unrouted - it resolved to
  // something - and not a seat we can report on. It is a routing table that disagrees with the
  // roster, which is a defect in the record and is surfaced rather than dropped.
  const known = new Set(list.map(lc => lc.scopeKey));
  const offRoster = cl.filter(c => {
    const sk = String((c && c.scopeKey) || '');
    return sk && !sk.endsWith('|UNROUTED') && !known.has(sk);
  });

  return {
    seats,
    vacantWithWork: seats.filter(s => s.state === 'VACANT' && s.claims.length > 0),
    vacantEmpty: seats.filter(s => s.state === 'VACANT' && s.claims.length === 0),
    notOfRecord: seats.filter(s => s.state === 'NOT_OF_RECORD'),
    held: seats.filter(s => s.state === 'HELD'),
    offRoster,
  };
}

// The unrouted queue: the work location resolved to no seat at all, so no Local Chairperson can see
// it and only the General Chairperson can fix it. Ordered soonest filing deadline first; a claim
// with NO computable deadline sorts AFTER every claim that has one, then oldest first. It must
// never look urgent and it must never look safe.
export function unroutedQueue(claims, opts) {
  const o = opts || {};
  const nowMs = typeof o.nowMs === 'number' ? o.nowMs : Date.now();
  return (Array.isArray(claims) ? claims : [])
    .filter(isUnrouted)
    .map(c => ({
      claim: c,
      left: daysUntilISO(c && c.expiresAt, nowMs),
      age: daysSince(claimFiledAtSeconds(c), nowMs),
    }))
    .sort((a, b) => {
      if (a.left === null && b.left === null) return (b.age || 0) - (a.age || 0);
      if (a.left === null) return 1;
      if (b.left === null) return -1;
      return a.left - b.left;
    });
}

// The arbitration docket and its clock.
//
// WATCHED vs NOT WATCHED is the whole design, and it mirrors checkArbitrationDocket() exactly
// rather than inventing a second convention (SS XI). A DEAD_FILE row is REPORTED BY NAME and is
// never silently skipped - the four rows on the docket today are REAL cases that a General
// Chairperson is handling OFF this platform, and a page that hid them would invite a future reader
// to conclude they were never there.
//
// `filed` is the field the clock keys on. Where submission_filed_date is present the row is filed
// and the due date is history. Where it is absent the due date is live - which is why the row's
// record_class matters so much that the verifier fails on a malformed one.
export function docketClock(docket, opts) {
  const o = opts || {};
  const nowMs = typeof o.nowMs === 'number' ? o.nowMs : Date.now();
  const soon = typeof o.soonDays === 'number' ? o.soonDays : DOCKET_SOON_DAYS;
  const rows = Array.isArray(docket) ? docket : ARBITRATION_DOCKET;

  const mapped = rows.map(r => {
    const cls = String((r && r.record_class) || '');
    const filed = String((r && r.submission_filed_date) || '').trim();
    const dueLeft = daysUntilISO(r && r.submission_due, nowMs);
    return {
      row: r,
      recordClass: cls,
      watched: cls === 'LIVE',
      filed: filed !== '',
      filedDate: filed || null,
      dueLeft,                                  // null when there is no due date on record
    };
  });

  // Malformed is its own bucket and is never treated as either value. Same posture the verifier
  // takes: a row whose class cannot be read is a row whose treatment cannot be decided.
  const malformed = mapped.filter(x => x.recordClass !== 'LIVE' && x.recordClass !== 'DEAD_FILE');
  const watched = mapped.filter(x => x.watched);
  const notWatched = mapped.filter(x => x.recordClass === 'DEAD_FILE');

  const overdue = watched.filter(x => !x.filed && x.dueLeft !== null && x.dueLeft < 0);
  const dueSoon = watched.filter(x => !x.filed && x.dueLeft !== null && x.dueLeft >= 0 && x.dueLeft <= soon)
                         .sort((a, b) => a.dueLeft - b.dueLeft);
  const noDueDate = watched.filter(x => !x.filed && x.dueLeft === null);

  return { rows: mapped, watched, notWatched, malformed, overdue, dueSoon, noDueDate, soonDays: soon };
}

// One call for the page. `opts` exists so the suite can pin `now` and inject a roster and a docket;
// production passes nothing.
// PLAN R-6 / finding G-1 (2026-09-21, approved): THE GENERAL CHAIRPERSON'S REVIEW INBOX.
// A Local Chairperson elevates a claim to `gc_review` and it has, until now, appeared in no block
// anywhere - it left his desk (deskQueue ELEVATED_LC shows him only that he sent it) and arrived on
// nobody's. This is the missing arrival.
//
// "DAYS WAITING" CANNOT BE COMPUTED, AND IS NOT FAKED. R-6's wording asks for days waiting, but
// deskQueue.js line 32 records the schema fact: there is no `elevatedBy` and no `elevatedAt`
// anywhere. Filing age is a DIFFERENT FACT and is not substituted for it - a claim filed in March
// and elevated this morning has not been waiting six months. So `waitingDays` is null and
// `waitingUnknown` is true, every time, until the schema carries an elevation timestamp. The rows
// are ordered by filing age because that is the only age on record, and the field is named
// `filedDaysAgo` so nothing downstream can read it as waiting time.
export function elevatedToGC(claims, opts) {
  const o = opts || {};
  const nowMs = o.nowMs || Date.now();
  return (Array.isArray(claims) ? claims : [])
    .filter((c) => c && c.status === 'gc_review')
    .map((c) => ({
      claim: c,
      filedDaysAgo: daysSince(claimFiledAtSeconds(c), nowMs),
      waitingDays: null,
      waitingUnknown: true,
      left: daysUntilISO(c && c.expiresAt, nowMs),
    }))
    // Soonest deadline first. A row with NO computable deadline sorts AFTER every row that has one,
    // then by filing age, oldest first - the same rule partitionDesk uses, for the same reason: it
    // must never appear urgent and must never appear safe.
    .sort((a, b) => {
      const an = a.left === null || a.left === undefined;
      const bn = b.left === null || b.left === undefined;
      if (an !== bn) return an ? 1 : -1;
      if (!an && a.left !== b.left) return a.left - b.left;
      return b.filedDaysAgo - a.filedDaysAgo;
    });
}

// T5 / D-18 (2026-10-15): THE GENERAL CHAIRPERSON'S DISCIPLINE INBOX. A Local Chairperson forwards a
// case (disciplineLadder.js 'forward': status -> gc_review, stamps forwardedToGCAt) and until now it
// appeared only on the case list and the case page - no block on The Committee said it was waiting
// on him. Unlike a claim (see elevatedToGC above), a forwarded case DOES carry the moment it was sent
// up, so the days since the forward are computed here - named forwardedDaysAgo, not waitingDays, which on this desk
// means the claims' never-known wait. A forwarded case with no readable stamp is not given a substitute age:
// forwardedDaysAgo is null and forwardUnknown is true.
// Waiting on him = status gc_review. A decided or resolved case has left the inbox.
function stampSeconds(t) {
  if (!t) return null;
  if (typeof t.seconds === 'number') return t.seconds;
  if (typeof t._seconds === 'number') return t._seconds;
  if (typeof t.toMillis === 'function') return Math.floor(t.toMillis() / 1000);
  const ms = typeof t === 'string' ? Date.parse(t) : NaN;
  return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
}

export function forwardedDisciplineToGC(cases, opts) {
  const o = opts || {};
  const nowMs = o.nowMs || Date.now();
  return (Array.isArray(cases) ? cases : [])
    .filter((k) => k && k.status === 'gc_review')
    .map((k) => {
      const s = stampSeconds(k.forwardedToGCAt);
      const forwardedDaysAgo = s === null ? null : daysSince(s, nowMs);
      return { kase: k, forwardedDaysAgo, forwardUnknown: forwardedDaysAgo === null, forwardedBy: k.forwardedToGCBy || null };
    })
    // Longest waiting first. A case whose wait is unknown sorts FIRST: it may be the oldest of all,
    // and putting it last would make it look recent.
    .sort((a, b) => {
      if (a.forwardUnknown !== b.forwardUnknown) return a.forwardUnknown ? -1 : 1;
      return (b.forwardedDaysAgo || 0) - (a.forwardedDaysAgo || 0);
    });
}

export function partitionGC(claims, opts) {
  const o = opts || {};
  const roster = o.roster || LC_ROSTER;
  const docket = o.docket || ARBITRATION_DOCKET;
  return {
    total: Array.isArray(claims) ? claims.length : 0,
    // R-6: first block. What was sent up to him and is waiting on him.
    elevated: elevatedToGC(claims, o),
    unrouted: unroutedQueue(claims, o),
    load: seatLoad(claims, roster, o),
    docket: docketClock(docket, o),
  };
}
